import React, { useState, useEffect } from 'react';
import { MEMBERSHIP_TYPES, getMembershipLabel, getMembershipColor } from './membershipTypes';
import { useAuth } from './AuthContext';

const emptyProgressForm = { id: null, course_slug: '', unit_label: '', unit_date: '', thema: '', note: '', ueben: '' };

function MemberList({ onEdit }) {
  const { authFetch } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('mitglieder'); // 'mitglieder' | 'feriencamp'
  const [sendingCredentials, setSendingCredentials] = useState(null);
  const [credentialsPassword, setCredentialsPassword] = useState('');

  // Ausgaben-Modal State
  const [expensesMember, setExpensesMember] = useState(null);
  const [memberBookings, setMemberBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [mealItems, setMealItems] = useState([]);
  const [newMealDesc, setNewMealDesc] = useState('');
  const [newMealAmount, setNewMealAmount] = useState('');
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [invoiceMessage, setInvoiceMessage] = useState(null);
  const [customPrice, setCustomPrice] = useState('');
  const [priceSuggestion, setPriceSuggestion] = useState(null);
  const [membershipStartDate, setMembershipStartDate] = useState('');

  // Buchung manuell nachtragen
  const [feriencampEvents, setFeriencampEvents] = useState([]);
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [newBookingEventId, setNewBookingEventId] = useState('');
  const [newBookingChildName, setNewBookingChildName] = useState('');
  const [newBookingChildAge, setNewBookingChildAge] = useState('');

  // Freie Rechnung State
  const [customInvoiceItems, setCustomInvoiceItems] = useState([]);
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');
  const [customInvoiceDiscount, setCustomInvoiceDiscount] = useState('');
  const [customInvoiceNotes, setCustomInvoiceNotes] = useState('');

  // Lernfortschritt-Modal State
  const [progressMember, setProgressMember] = useState(null);
  const [progressNotes, setProgressNotes] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressForm, setProgressForm] = useState(emptyProgressForm);
  const [progressSaving, setProgressSaving] = useState(false);
  const [progressError, setProgressError] = useState('');

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await authFetch('/api/members');
      const result = await response.json();
      if (result.data) {
        setMembers(result.data);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Möchten Sie "${name}" wirklich löschen?`)) {
      try {
        const response = await authFetch(`/api/members/${id}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          setMembers(members.filter(m => m.id !== id));
        }
      } catch (error) {
        console.error('Error deleting member:', error);
      }
    }
  };

  const handleSendCredentials = async () => {
    // Wenn kein pending_password und kein neues Passwort eingegeben wurde
    if (!sendingCredentials.pending_password && (!credentialsPassword || credentialsPassword.length < 6)) {
      alert('Bitte ein Passwort mit mindestens 6 Zeichen eingeben');
      return;
    }

    try {
      const response = await authFetch(`/api/members/${sendingCredentials.id}/send-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: credentialsPassword || undefined })
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Zugangsdaten wurden an ${sendingCredentials.email} gesendet!`);
        setSendingCredentials(null);
        setCredentialsPassword('');
        fetchMembers(); // Liste aktualisieren, falls sich pending_password geaendert hat
      } else {
        alert('Fehler: ' + (result.error || 'E-Mail konnte nicht gesendet werden'));
      }
    } catch (error) {
      console.error('Error sending credentials:', error);
      alert('Netzwerkfehler beim Senden der Zugangsdaten');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  // Lernfortschritt-Modal Funktionen
  const loadProgressNotes = async (memberId) => {
    setProgressLoading(true);
    try {
      const response = await authFetch(`/api/members/${memberId}/course-notes`);
      const result = await response.json();
      setProgressNotes(result.data || []);
    } catch (err) {
      console.error('Error loading progress notes:', err);
    } finally {
      setProgressLoading(false);
    }
  };

  const openProgressModal = (member) => {
    setProgressMember(member);
    setProgressForm(emptyProgressForm);
    setProgressError('');
    loadProgressNotes(member.id);
  };

  const closeProgressModal = () => {
    setProgressMember(null);
    setProgressNotes([]);
    setProgressForm(emptyProgressForm);
    setProgressError('');
  };

  const handleProgressEdit = (note) => {
    setProgressForm({
      id: note.id,
      course_slug: note.course_slug || '',
      unit_label: note.unit_label || '',
      unit_date: note.unit_date || '',
      thema: note.thema || '',
      note: note.note || '',
      ueben: note.ueben || ''
    });
    setProgressError('');
  };

  const handleProgressSubmit = async (e) => {
    e.preventDefault();
    setProgressError('');

    if (!progressForm.course_slug.trim() || !progressForm.unit_label.trim()) {
      setProgressError('Bitte Kurs und Termin/Einheit angeben.');
      return;
    }

    setProgressSaving(true);
    try {
      const isEdit = !!progressForm.id;
      const url = isEdit
        ? `/api/course-notes/${progressForm.id}`
        : `/api/members/${progressMember.id}/course-notes`;
      const response = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        body: progressForm
      });
      const result = await response.json();
      if (response.ok) {
        setProgressForm({ ...emptyProgressForm, course_slug: progressForm.course_slug });
        loadProgressNotes(progressMember.id);
      } else {
        setProgressError(result.error || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      setProgressError('Netzwerkfehler beim Speichern');
    } finally {
      setProgressSaving(false);
    }
  };

  const handleProgressDelete = async (noteId) => {
    if (!window.confirm('Eintrag wirklich loeschen?')) return;
    try {
      const response = await authFetch(`/api/course-notes/${noteId}`, { method: 'DELETE' });
      if (response.ok) {
        loadProgressNotes(progressMember.id);
        if (progressForm.id === noteId) setProgressForm(emptyProgressForm);
      }
    } catch (err) {
      alert('Fehler beim Loeschen');
    }
  };

  // Ausgaben-Modal Funktionen
  const loadMemberBookings = async (member) => {
    try {
      // Buchungen basierend auf E-Mail laden
      const response = await fetch('/api/feriencamp-bookings');
      const result = await response.json();
      const bookings = (result.data || []).filter(b =>
        b.email?.toLowerCase() === member.email?.toLowerCase()
      );
      setMemberBookings(bookings);
      return bookings;
    } catch (err) {
      console.error('Error loading bookings:', err);
      return [];
    }
  };

  const openExpensesModal = async (member) => {
    setExpensesMember(member);
    setSelectedBooking(null);
    setMealItems([]);
    setInvoiceMessage(null);
    setShowAddBooking(false);
    setNewBookingEventId('');
    setNewBookingChildName('');
    setNewBookingChildAge('');
    setMembershipStartDate('');
    setExpensesLoading(true);

    try {
      await loadMemberBookings(member);
      const eventsResponse = await fetch('/api/feriencamp-events');
      const eventsResult = await eventsResponse.json();
      setFeriencampEvents(eventsResult.data || []);
    } finally {
      setExpensesLoading(false);
    }
  };

  const addManualBooking = async () => {
    if (!newBookingEventId || !newBookingChildName || !expensesMember) return;

    setExpensesLoading(true);
    setInvoiceMessage(null);

    try {
      const response = await fetch('/api/feriencamp-bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: parseInt(newBookingEventId),
          parent_name: `${expensesMember.first_name} ${expensesMember.last_name}`,
          email: expensesMember.email,
          phone: expensesMember.phone || null,
          child_name: newBookingChildName,
          child_age: newBookingChildAge ? parseInt(newBookingChildAge) : null
        })
      });
      const result = await response.json();

      if (result.message === 'success') {
        setShowAddBooking(false);
        setNewBookingEventId('');
        setNewBookingChildName('');
        setNewBookingChildAge('');
        const bookings = await loadMemberBookings(expensesMember);
        const created = bookings.find(b => b.id === result.data.id);
        if (created) selectBooking(created);
        setInvoiceMessage({ type: 'success', text: 'Buchung hinzugefuegt!' });
      } else {
        setInvoiceMessage({ type: 'error', text: result.error || 'Fehler beim Hinzufuegen' });
      }
    } catch (err) {
      setInvoiceMessage({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setExpensesLoading(false);
    }
  };

  // Geschwister = andere (nicht stornierte) Buchungen derselben Familie fuer dasselbe Camp,
  // sortiert nach Anmeldezeitpunkt (das zuerst angemeldete Kind zahlt den vollen Preis).
  const getFamilyBookingsForEvent = (booking) => {
    return memberBookings
      .filter(b => b.event_id === booking.event_id && b.status !== 'cancelled')
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id - b.id);
  };

  // Ermittelt automatisch, ob Geschwister- und/oder Fruehbucherrabatt greifen, und schlaegt den passenden Preis vor.
  const getPriceSuggestion = (booking) => {
    const siblings = getFamilyBookingsForEvent(booking);
    const isSibling = siblings.findIndex(b => b.id === booking.id) > 0;

    const isEarlyBird = !!(
      booking.early_bird_deadline &&
      booking.price_early_bird &&
      booking.created_at &&
      new Date(booking.created_at) <= new Date(booking.early_bird_deadline)
    );

    // Vom spezifischsten zum allgemeinsten Preis durchprobieren
    const candidates = [];
    if (isEarlyBird && isSibling) candidates.push(booking.price_early_bird_sibling);
    if (isEarlyBird) candidates.push(booking.price_early_bird);
    if (isSibling) candidates.push(booking.price_sibling);
    candidates.push(booking.price);
    const suggestedPrice = candidates.find(v => v !== null && v !== undefined && v !== '');

    const reasons = [];
    if (isEarlyBird) reasons.push('Frühbucher');
    if (isSibling) reasons.push('Geschwisterrabatt');

    return {
      price: suggestedPrice !== undefined ? parseFloat(suggestedPrice) : null,
      isSibling,
      isEarlyBird,
      reasons
    };
  };

  const selectBooking = async (booking) => {
    setSelectedBooking(booking);

    const suggestion = getPriceSuggestion(booking);
    setPriceSuggestion(suggestion);
    setCustomPrice(suggestion.price !== null ? suggestion.price : (booking.price || ''));

    try {
      const response = await fetch(`/api/feriencamp-bookings/${booking.id}/meals`);
      const result = await response.json();
      setMealItems(result.data || []);
    } catch (err) {
      console.error('Error loading meals:', err);
    }
  };

  const addMealItem = async () => {
    if (!newMealDesc || !newMealAmount || !selectedBooking) return;

    try {
      const response = await fetch(`/api/feriencamp-bookings/${selectedBooking.id}/meals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: newMealDesc,
          amount: parseFloat(newMealAmount)
        })
      });
      const result = await response.json();
      if (result.message === 'success') {
        setMealItems([...mealItems, result.data]);
        setNewMealDesc('');
        setNewMealAmount('');
      }
    } catch (err) {
      console.error('Error adding meal:', err);
    }
  };

  const deleteMealItem = async (mealId) => {
    if (!window.confirm('Position loeschen?')) return;

    try {
      await fetch(`/api/feriencamp-meals/${mealId}`, { method: 'DELETE' });
      setMealItems(mealItems.filter(m => m.id !== mealId));
    } catch (err) {
      console.error('Error deleting meal:', err);
    }
  };

  // Erstellt automatisch eine Camp-Rechnung und, falls Essen-Positionen vorhanden sind,
  // zusaetzlich eine eigene Essen-Rechnung (ohne MwSt.) - kein manuelles Umschalten noetig.
  const createInvoiceForBooking = async (booking) => {
    setExpensesLoading(true);
    setInvoiceMessage(null);

    const hasMeals = mealItems.length > 0;

    try {
      const campResponse = await fetch(`/api/invoices/feriencamp-booking/${booking.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customPrice: parseFloat(customPrice) || null, includeMeals: !hasMeals })
      });
      const campResult = await campResponse.json();

      if (campResult.message !== 'success') {
        setInvoiceMessage({ type: 'error', text: campResult.error || 'Fehler beim Erstellen der Camp-Rechnung' });
        return;
      }

      const messages = [`Camp-Rechnung ${campResult.data.invoice_number} erstellt!`];

      if (hasMeals) {
        const foodResponse = await fetch(`/api/invoices/feriencamp-food/${booking.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        const foodResult = await foodResponse.json();
        if (foodResult.message === 'success') {
          messages.push(`Essen-Rechnung ${foodResult.data.invoice_number} erstellt!`);
        } else {
          messages.push(`Essen-Rechnung fehlgeschlagen: ${foodResult.error || 'Fehler'}`);
        }
      }

      setInvoiceMessage({ type: 'success', text: messages.join(' ') });
    } catch (err) {
      setInvoiceMessage({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setExpensesLoading(false);
    }
  };

  const getMealTotal = () => {
    return mealItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  };

  // Freie Rechnung Funktionen
  const addCustomItem = () => {
    if (!newItemDesc || !newItemAmount) return;
    setCustomInvoiceItems([...customInvoiceItems, {
      id: Date.now(),
      description: newItemDesc,
      amount: parseFloat(newItemAmount)
    }]);
    setNewItemDesc('');
    setNewItemAmount('');
  };

  const deleteCustomItem = (itemId) => {
    setCustomInvoiceItems(customInvoiceItems.filter(item => item.id !== itemId));
  };

  const getCustomInvoiceTotal = () => {
    const subtotal = customInvoiceItems.reduce((sum, item) => sum + item.amount, 0);
    const discount = parseFloat(customInvoiceDiscount) || 0;
    return subtotal - discount;
  };

  const createCustomInvoice = async () => {
    if (!expensesMember || customInvoiceItems.length === 0) return;

    setExpensesLoading(true);
    setInvoiceMessage(null);

    try {
      const response = await authFetch(`/api/invoices/member/${expensesMember.id}`, {
        method: 'POST',
        body: {
          items: customInvoiceItems,
          discount: parseFloat(customInvoiceDiscount) || 0,
          notes: customInvoiceNotes || null
        }
      });
      const result = await response.json();

      if (result.message === 'success') {
        setInvoiceMessage({ type: 'success', text: `Rechnung ${result.data.invoice_number} erstellt!` });
        setCustomInvoiceItems([]);
        setCustomInvoiceDiscount('');
        setCustomInvoiceNotes('');
      } else {
        setInvoiceMessage({ type: 'error', text: result.error || 'Fehler beim Erstellen' });
      }
    } catch (err) {
      setInvoiceMessage({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setExpensesLoading(false);
    }
  };

  // Anteiligen Beitrag fuer den ersten Monat berechnen, wenn der Start nicht der 1. ist
  const getMembershipInvoiceItem = (type, startDateStr) => {
    const fullAmount = parseFloat(type.price) || 0;

    if (!startDateStr || type.priceType !== 'Monat') {
      return { description: `${type.name} (${type.priceType})`, amount: fullAmount, isProrated: false };
    }

    const [year, month, day] = startDateStr.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();

    if (day <= 1) {
      return { description: `${type.name} (${type.priceType})`, amount: fullAmount, isProrated: false };
    }

    const remainingDays = daysInMonth - day + 1;
    const amount = Math.round(fullAmount * remainingDays / daysInMonth * 100) / 100;

    return {
      description: `${type.name} (anteilig ab ${formatDate(startDateStr)}, ${remainingDays}/${daysInMonth} Tage)`,
      amount,
      isProrated: true,
      remainingDays,
      daysInMonth
    };
  };

  const createMembershipInvoice = async () => {
    const type = MEMBERSHIP_TYPES[expensesMember?.membership_type];
    if (!expensesMember || !type) return;

    const item = getMembershipInvoiceItem(type, membershipStartDate);
    setExpensesLoading(true);
    setInvoiceMessage(null);

    try {
      const response = await authFetch(`/api/invoices/member/${expensesMember.id}`, {
        method: 'POST',
        body: {
          items: [{ description: item.description, amount: item.amount }]
        }
      });
      const result = await response.json();

      if (result.message === 'success') {
        setInvoiceMessage({ type: 'success', text: `Rechnung ${result.data.invoice_number} erstellt!` });
        setMembershipStartDate('');
      } else {
        setInvoiceMessage({ type: 'error', text: result.error || 'Fehler beim Erstellen' });
      }
    } catch (err) {
      setInvoiceMessage({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setExpensesLoading(false);
    }
  };

  const mitgliederMembers = members.filter(m => m.membership_type !== 'feriencamp');
  const feriencampMembers = members.filter(m => m.membership_type === 'feriencamp');

  const filteredMembers = (viewMode === 'feriencamp' ? feriencampMembers : mitgliederMembers).filter(member => {
    const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
    const email = (member.email || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  if (loading) {
    return <div className="text-center py-4">Laden...</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>{viewMode === 'feriencamp' ? 'Feriencamp-Zugänge' : 'Mitglieder-Übersicht'}</h4>
        <span className="badge bg-secondary">
          {viewMode === 'feriencamp' ? feriencampMembers.length : mitgliederMembers.length}{' '}
          {viewMode === 'feriencamp' ? 'Feriencamp-Zugänge' : 'Mitglieder'}
        </span>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${viewMode === 'mitglieder' ? 'active' : ''}`}
            onClick={() => setViewMode('mitglieder')}
          >
            Mitglieder <span className="badge bg-secondary ms-1">{mitgliederMembers.length}</span>
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${viewMode === 'feriencamp' ? 'active' : ''}`}
            onClick={() => setViewMode('feriencamp')}
          >
            Feriencamp-Zugänge <span className="badge bg-secondary ms-1">{feriencampMembers.length}</span>
          </button>
        </li>
      </ul>
      {viewMode === 'feriencamp' && (
        <div className="alert alert-info py-2 small">
          Automatisch erstellte Portal-Accounts für Feriencamp-Familien (Zugang zu Feriencamp-Infos, kein reguläres Mitgliedschaftskonto).
        </div>
      )}

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Suchen nach Name oder E-Mail..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {filteredMembers.length === 0 ? (
        <div className="alert alert-info">
          {viewMode === 'feriencamp'
            ? (feriencampMembers.length === 0
                ? 'Noch keine Feriencamp-Zugänge vorhanden.'
                : 'Keine Feriencamp-Zugänge gefunden.')
            : (mitgliederMembers.length === 0
                ? 'Noch keine Mitglieder vorhanden. Erstellen Sie ein neues Mitglied.'
                : 'Keine Mitglieder gefunden.')}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Mitgliedschaft</th>
                <th>E-Mail</th>
                <th>Telefon</th>
                <th>Ort</th>
                <th>Login</th>
                <th>IFS</th>
                <th style={{ width: '120px' }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map(member => (
                <tr key={member.id}>
                  <td>
                    <strong>{member.last_name}</strong>, {member.first_name}
                  </td>
                  <td>
                    {member.membership_type ? (
                      <span
                        className="badge"
                        style={{ backgroundColor: getMembershipColor(member.membership_type) }}
                      >
                        {getMembershipLabel(member.membership_type)}
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>{member.email || '-'}</td>
                  <td>{member.phone || '-'}</td>
                  <td>{member.city || '-'}</td>
                  <td>
                    {member.username ? (
                      <span className="badge bg-success" title={`Benutzername: ${member.username}`}>Ja</span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {member.internetfuehrerschein_enabled ? (
                      <span className="badge bg-info" title="Internetfuehrerschein freigeschaltet">Ja</span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => onEdit(member)}
                      title="Bearbeiten"
                    >
                      Bearbeiten
                    </button>
                    <button
                      className="btn btn-sm btn-outline-warning me-1"
                      onClick={() => openExpensesModal(member)}
                      title="Ausgaben & Rechnung"
                    >
                      EUR
                    </button>
                    {member.username && member.email && (
                      <button
                        className="btn btn-sm btn-outline-success me-1"
                        onClick={() => setSendingCredentials(member)}
                        title="Zugangsdaten per E-Mail senden"
                      >
                        Mail
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-info me-1"
                      onClick={() => openProgressModal(member)}
                      title="Lernfortschritt & Uebungsaufgaben"
                    >
                      Fortschritt
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(member.id, `${member.first_name} ${member.last_name}`)}
                      title="Loeschen"
                    >
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal für Zugangsdaten senden */}
      {sendingCredentials && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Zugangsdaten senden</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setSendingCredentials(null);
                    setCredentialsPassword('');
                  }}
                />
              </div>
              <div className="modal-body">
                <p>
                  Zugangsdaten werden an <strong>{sendingCredentials.email}</strong> gesendet für:
                </p>
                <p className="mb-3">
                  <strong>{sendingCredentials.first_name} {sendingCredentials.last_name}</strong><br />
                  <small className="text-muted">Benutzername: {sendingCredentials.username}</small>
                </p>
                <div className="mb-3">
                  <label className="form-label">Passwort:</label>
                  {sendingCredentials.pending_password ? (
                    <div>
                      <div className="alert alert-success py-2">
                        <strong>Gespeichertes Passwort:</strong> {sendingCredentials.pending_password}
                      </div>
                      <small className="text-muted">
                        Das bereits vergebene Passwort wird verwendet. Sie koennen es unten ueberschreiben.
                      </small>
                      <div className="input-group mt-2">
                        <input
                          type="text"
                          className="form-control"
                          value={credentialsPassword}
                          onChange={(e) => setCredentialsPassword(e.target.value)}
                          placeholder="Neues Passwort (optional)"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={credentialsPassword}
                          onChange={(e) => setCredentialsPassword(e.target.value)}
                          placeholder="Passwort (min. 6 Zeichen)"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
                            let pwd = '';
                            for (let i = 0; i < 8; i++) {
                              pwd += chars.charAt(Math.floor(Math.random() * chars.length));
                            }
                            setCredentialsPassword(pwd);
                          }}
                        >
                          Generieren
                        </button>
                      </div>
                      <small className="text-muted">
                        Das Passwort wird im Account gesetzt und in der E-Mail angezeigt.
                      </small>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSendingCredentials(null);
                    setCredentialsPassword('');
                  }}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSendCredentials}
                  disabled={!sendingCredentials.pending_password && (!credentialsPassword || credentialsPassword.length < 6)}
                >
                  E-Mail senden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal fuer Lernfortschritt & Uebungsaufgaben */}
      {progressMember && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Lernfortschritt: {progressMember.first_name} {progressMember.last_name}
                </h5>
                <button type="button" className="btn-close" onClick={closeProgressModal}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small">
                  Trage hier pro Kurs und Termin ein, was ihr gemacht habt und welche Uebungsaufgabe es gibt.
                  Eltern sehen diese Eintraege nach dem Login im Mitgliederbereich.
                </p>

                {progressError && <div className="alert alert-danger py-2">{progressError}</div>}

                {progressLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status"></div>
                  </div>
                ) : progressNotes.length === 0 ? (
                  <p className="text-muted">Noch keine Eintraege vorhanden.</p>
                ) : (
                  <div className="table-responsive mb-3">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Kurs</th>
                          <th>Termin</th>
                          <th>Datum</th>
                          <th>Thema</th>
                          <th>Uebung</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {progressNotes.map(n => (
                          <tr key={n.id}>
                            <td>{n.course_slug}</td>
                            <td>{n.unit_label}</td>
                            <td>{n.unit_date ? formatDate(n.unit_date) : '-'}</td>
                            <td>{n.thema || '-'}</td>
                            <td>{n.ueben || '-'}</td>
                            <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                              <button
                                className="btn btn-sm btn-outline-secondary py-0 px-1 me-1"
                                onClick={() => handleProgressEdit(n)}
                                title="Bearbeiten"
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger py-0 px-1"
                                onClick={() => handleProgressDelete(n.id)}
                                title="Loeschen"
                              >
                                x
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <form onSubmit={handleProgressSubmit} className="border-top pt-3">
                  <h6>{progressForm.id ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</h6>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <label className="form-label small">Kurs *</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="z.B. Scratch-Kurs, Programmieren, KI fuer Kids"
                        list="progress-course-suggestions"
                        value={progressForm.course_slug}
                        onChange={(e) => setProgressForm(prev => ({ ...prev, course_slug: e.target.value }))}
                      />
                      <datalist id="progress-course-suggestions">
                        {[...new Set(progressNotes.map(n => n.course_slug))].map(slug => (
                          <option key={slug} value={slug} />
                        ))}
                      </datalist>
                    </div>
                    <div className="col-md-3 mb-2">
                      <label className="form-label small">Termin/Einheit *</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="z.B. Einheit 3"
                        value={progressForm.unit_label}
                        onChange={(e) => setProgressForm(prev => ({ ...prev, unit_label: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <label className="form-label small">Datum</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={progressForm.unit_date}
                        onChange={(e) => setProgressForm(prev => ({ ...prev, unit_date: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label small">Thema / Was wir gemacht haben</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="2"
                      value={progressForm.thema}
                      onChange={(e) => setProgressForm(prev => ({ ...prev, thema: e.target.value }))}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small">Uebungsaufgabe</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="2"
                      value={progressForm.ueben}
                      onChange={(e) => setProgressForm(prev => ({ ...prev, ueben: e.target.value }))}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small">Interne Notiz (nicht fuer Eltern sichtbar)</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="2"
                      value={progressForm.note}
                      onChange={(e) => setProgressForm(prev => ({ ...prev, note: e.target.value }))}
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-sm btn-primary" disabled={progressSaving}>
                      {progressSaving ? 'Speichern...' : (progressForm.id ? 'Aktualisieren' : 'Hinzufuegen')}
                    </button>
                    {progressForm.id && (
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => setProgressForm(emptyProgressForm)}
                      >
                        Abbrechen
                      </button>
                    )}
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeProgressModal}>
                  Schliessen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal fuer Ausgaben */}
      {expensesMember && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Ausgaben - {expensesMember.first_name} {expensesMember.last_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setExpensesMember(null);
                    setMemberBookings([]);
                    setSelectedBooking(null);
                    setMealItems([]);
                    setPriceSuggestion(null);
                    setCustomInvoiceItems([]);
                    setCustomInvoiceDiscount('');
                    setCustomInvoiceNotes('');
                    setMembershipStartDate('');
                    setInvoiceMessage(null);
                  }}
                />
              </div>
              <div className="modal-body">
                {invoiceMessage && (
                  <div className={`alert alert-${invoiceMessage.type === 'success' ? 'success' : 'danger'} alert-dismissible`}>
                    {invoiceMessage.text}
                    <button type="button" className="btn-close" onClick={() => setInvoiceMessage(null)}></button>
                  </div>
                )}

                {expensesLoading ? (
                  <div className="text-center py-4">Laden...</div>
                ) : (
                  <>
                    {/* Mitgliedschaft erkannt */}
                    {expensesMember.membership_type !== 'feriencamp' && MEMBERSHIP_TYPES[expensesMember.membership_type] && (() => {
                      const membershipType = MEMBERSHIP_TYPES[expensesMember.membership_type];
                      const item = getMembershipInvoiceItem(membershipType, membershipStartDate);
                      return (
                        <div className="card mb-4 border-success">
                          <div className="card-header bg-success text-white">
                            <strong>Mitgliedschaft erkannt: {membershipType.name}</strong>
                          </div>
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                {membershipType.name}
                                {' '}&middot;{' '}
                                {(parseFloat(membershipType.price) || 0).toFixed(2)} EUR
                                {' / '}
                                {membershipType.priceType}
                              </div>
                            </div>

                            {membershipType.priceType === 'Monat' && (
                              <div className="mb-3">
                                <label className="form-label small">
                                  Beitritt während des Monats? Startdatum eintragen (sonst voller Monatsbeitrag):
                                </label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  style={{ maxWidth: '200px' }}
                                  value={membershipStartDate}
                                  onChange={(e) => setMembershipStartDate(e.target.value)}
                                />
                                {item.isProrated && (
                                  <small className="text-success d-block mt-1">
                                    ✓ Anteilig berechnet: {item.remainingDays} von {item.daysInMonth} Tagen
                                  </small>
                                )}
                              </div>
                            )}

                            <div className="d-flex justify-content-between align-items-center border-top pt-2">
                              <small className="text-muted">{item.description}</small>
                              <button
                                className="btn btn-success"
                                onClick={createMembershipInvoice}
                                disabled={expensesLoading}
                              >
                                Rechnung erstellen ({item.amount.toFixed(2)} EUR)
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Freie Rechnung erstellen */}
                    <div className="card mb-4">
                      <div className="card-header bg-primary text-white">
                        <strong>Freie Rechnung erstellen</strong>
                      </div>
                      <div className="card-body">
                        {/* Neue Position hinzufuegen */}
                        <div className="row g-2 align-items-end mb-3">
                          <div className="col">
                            <label className="form-label small">Beschreibung</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="z.B. Kursgebuehr, Material, etc."
                              value={newItemDesc}
                              onChange={(e) => setNewItemDesc(e.target.value)}
                            />
                          </div>
                          <div className="col-3">
                            <label className="form-label small">Betrag (EUR)</label>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              placeholder="0.00"
                              step="0.01"
                              value={newItemAmount}
                              onChange={(e) => setNewItemAmount(e.target.value)}
                            />
                          </div>
                          <div className="col-auto">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={addCustomItem}
                              disabled={!newItemDesc || !newItemAmount}
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Positionen-Liste */}
                        {customInvoiceItems.length > 0 ? (
                          <>
                            <table className="table table-sm mb-3">
                              <tbody>
                                {customInvoiceItems.map(item => (
                                  <tr key={item.id}>
                                    <td>{item.description}</td>
                                    <td className="text-end">{item.amount.toFixed(2)} EUR</td>
                                    <td style={{ width: '30px' }}>
                                      <button
                                        className="btn btn-sm btn-outline-danger py-0 px-1"
                                        onClick={() => deleteCustomItem(item.id)}
                                      >
                                        x
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {/* Rabatt */}
                            <div className="row g-2 align-items-center mb-3">
                              <div className="col-auto">
                                <label className="form-label small mb-0">Rabatt (EUR):</label>
                              </div>
                              <div className="col-3">
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  step="0.01"
                                  value={customInvoiceDiscount}
                                  onChange={(e) => setCustomInvoiceDiscount(e.target.value)}
                                  placeholder="0.00"
                                />
                              </div>
                            </div>

                            {/* Summe */}
                            <div className="d-flex justify-content-between align-items-center border-top pt-2 mb-3">
                              <strong>Gesamtbetrag:</strong>
                              <strong className="text-success">{getCustomInvoiceTotal().toFixed(2)} EUR</strong>
                            </div>

                            {/* Notizen */}
                            <div className="mb-3">
                              <label className="form-label small">Notizen (optional)</label>
                              <textarea
                                className="form-control form-control-sm"
                                rows="2"
                                value={customInvoiceNotes}
                                onChange={(e) => setCustomInvoiceNotes(e.target.value)}
                                placeholder="Zusaetzliche Hinweise fuer die Rechnung..."
                              />
                            </div>

                            <button
                              className="btn btn-success"
                              onClick={createCustomInvoice}
                              disabled={expensesLoading || customInvoiceItems.length === 0}
                            >
                              Rechnung erstellen
                            </button>
                          </>
                        ) : (
                          <p className="text-muted mb-0">
                            Fuege Positionen hinzu, um eine freie Rechnung zu erstellen.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Feriencamp-Buchungen */}
                    <div className="card">
                        <div className="card-header bg-secondary text-white">
                          <strong>Feriencamp-Buchungen</strong>
                        </div>
                        <div className="card-body">
                  <div className="row">
                    {/* Buchungen-Liste links */}
                    <div className="col-md-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Buchungen</h6>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setShowAddBooking(!showAddBooking)}
                        >
                          {showAddBooking ? 'Abbrechen' : '+ Buchung'}
                        </button>
                      </div>

                      {showAddBooking && (
                        <div className="card mb-3">
                          <div className="card-body p-2">
                            <select
                              className="form-select form-select-sm mb-2"
                              value={newBookingEventId}
                              onChange={(e) => setNewBookingEventId(e.target.value)}
                            >
                              <option value="">Camp waehlen...</option>
                              {feriencampEvents.map(ev => (
                                <option key={ev.id} value={ev.id}>
                                  {ev.title} ({formatDate(ev.start_date)})
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              className="form-control form-control-sm mb-2"
                              placeholder="Name des Kindes"
                              value={newBookingChildName}
                              onChange={(e) => setNewBookingChildName(e.target.value)}
                            />
                            <input
                              type="number"
                              className="form-control form-control-sm mb-2"
                              placeholder="Alter (optional)"
                              value={newBookingChildAge}
                              onChange={(e) => setNewBookingChildAge(e.target.value)}
                            />
                            <button
                              className="btn btn-sm btn-primary w-100"
                              onClick={addManualBooking}
                              disabled={expensesLoading || !newBookingEventId || !newBookingChildName}
                            >
                              Hinzufuegen
                            </button>
                          </div>
                        </div>
                      )}

                      {memberBookings.length > 0 ? (
                        <div className="list-group">
                          {memberBookings.map(booking => (
                            <button
                              key={booking.id}
                              className={`list-group-item list-group-item-action ${selectedBooking?.id === booking.id ? 'active' : ''}`}
                              onClick={() => selectBooking(booking)}
                            >
                              <strong>{booking.child_name}</strong>
                              <br />
                              <small>{booking.event_title}</small>
                              <br />
                              <small className="text-muted">{formatDate(booking.start_date)}</small>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted small">Noch keine Buchungen.</p>
                      )}
                    </div>

                    {/* Essen-Positionen rechts */}
                    <div className="col-md-8">
                      {selectedBooking ? (
                        <div>
                          <h6>Essen fuer {selectedBooking.child_name}</h6>
                          <p className="text-muted small mb-2">
                            {selectedBooking.event_title}
                          </p>

                          {/* Camp-Preis (automatisch erkannter Fruehbucher-/Geschwisterrabatt, editierbar) */}
                          <div className="mb-3">
                            <label className="form-label small fw-bold">Camp-Preis (EUR):</label>
                            <div className="input-group input-group-sm" style={{ maxWidth: '200px' }}>
                              <input
                                type="number"
                                className="form-control"
                                step="0.01"
                                value={customPrice}
                                onChange={(e) => setCustomPrice(e.target.value)}
                                placeholder="z.B. 155 fuer Geschwister"
                              />
                              <span className="input-group-text">EUR</span>
                            </div>
                            {priceSuggestion?.reasons?.length > 0 ? (
                              <small className="text-success d-block">
                                ✓ Automatisch erkannt: {priceSuggestion.reasons.join(' + ')}
                                {' '}(Normalpreis: {parseFloat(selectedBooking.price || 0).toFixed(2)} EUR)
                              </small>
                            ) : (
                              <small className="text-muted">
                                Normalpreis: {parseFloat(selectedBooking.price || 0).toFixed(2)} EUR (bei Bedarf anpassen)
                              </small>
                            )}
                          </div>

                          {/* Neue Position */}
                          <div className="card mb-3">
                            <div className="card-body py-2">
                              <div className="row g-2 align-items-end">
                                <div className="col">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Beschreibung"
                                    value={newMealDesc}
                                    onChange={(e) => setNewMealDesc(e.target.value)}
                                  />
                                </div>
                                <div className="col-3">
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    placeholder="EUR"
                                    step="0.01"
                                    value={newMealAmount}
                                    onChange={(e) => setNewMealAmount(e.target.value)}
                                  />
                                </div>
                                <div className="col-auto">
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={addMealItem}
                                    disabled={!newMealDesc || !newMealAmount}
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Liste */}
                          {mealItems.length > 0 ? (
                            <table className="table table-sm">
                              <tbody>
                                {mealItems.map(item => (
                                  <tr key={item.id}>
                                    <td>{item.description}</td>
                                    <td className="text-end">{parseFloat(item.amount).toFixed(2)} EUR</td>
                                    <td style={{ width: '30px' }}>
                                      <button
                                        className="btn btn-sm btn-outline-danger py-0 px-1"
                                        onClick={() => deleteMealItem(item.id)}
                                      >
                                        x
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                                <tr className="table-secondary">
                                  <td><strong>Essen gesamt</strong></td>
                                  <td className="text-end"><strong>{getMealTotal().toFixed(2)} EUR</strong></td>
                                  <td></td>
                                </tr>
                                <tr className="table-info">
                                  <td><strong>Gesamt (2 Rechnungen: Camp + Essen)</strong></td>
                                  <td className="text-end">
                                    <strong>{(parseFloat(customPrice || 0) + getMealTotal()).toFixed(2)} EUR</strong>
                                  </td>
                                  <td></td>
                                </tr>
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-muted">Noch keine Essen-Positionen.</p>
                          )}

                          {mealItems.length > 0 && (
                            <small className="text-muted d-block mb-2">
                              Essen wird automatisch als eigene Rechnung ohne MwSt. erstellt.
                            </small>
                          )}

                          {/* Rechnung erstellen */}
                          <button
                            className="btn btn-success mt-2"
                            onClick={() => createInvoiceForBooking(selectedBooking)}
                            disabled={expensesLoading}
                          >
                            {mealItems.length > 0 ? 'Rechnungen erstellen (Camp + Essen)' : 'Rechnung erstellen'}
                          </button>
                        </div>
                      ) : (
                        <div className="text-muted">
                          Waehle links eine Buchung aus.
                        </div>
                      )}
                    </div>
                  </div>
                        </div>
                      </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setExpensesMember(null);
                    setMemberBookings([]);
                    setSelectedBooking(null);
                    setMealItems([]);
                    setPriceSuggestion(null);
                    setCustomInvoiceItems([]);
                    setCustomInvoiceDiscount('');
                    setCustomInvoiceNotes('');
                    setMembershipStartDate('');
                    setInvoiceMessage(null);
                  }}
                >
                  Schliessen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MemberList;
